import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useDropzone } from "react-dropzone";
import { Box, TextField, Typography, IconButton, Paper } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useSetRecoilState } from "recoil";
import { routeInfo } from "recoilState/GlobalState";
import { Routes } from "consts/Routes-App";
import { Button } from "components";
import styles from "./PdfAnnotationEditor.module.scss";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { PDFArray, PDFDocument, PDFHexString, PDFName, PDFNumber, PDFString, rgb } from "pdf-lib";

// Worker debe coincidir con la versión de pdfjs que usa react-pdf.
// pdfjs-dist 3.x usa .js; pdfjs-dist 4.x usa .mjs
const workerExt = pdfjs.version.startsWith("4") || parseInt(pdfjs.version, 10) >= 4 ? "mjs" : "js";
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.${workerExt}`;

const createId = () => `ann-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const PdfAnnotationEditor = () => {
    const setRoute = useSetRecoilState(routeInfo);
    const [file, setFile] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [annotations, setAnnotations] = useState([]);
    const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
    const [drawing, setDrawing] = useState(null);
    const pageRefs = useRef({});

    const loadExistingAnnotationsFromPdf = useCallback(
        async (currentFile, currentNumPages) => {
            if (!currentFile || !currentNumPages) return;

            try {
                const existingPdfBytes = await currentFile.arrayBuffer();
                const pdfDoc = await PDFDocument.load(existingPdfBytes);

                const loadedAnnotations = [];

                for (let pageIndex = 0; pageIndex < currentNumPages; pageIndex++) {
                    const pageNum = pageIndex + 1;
                    const page = pdfDoc.getPage(pageIndex);
                    if (!page) continue;

                    const annots = page.node.lookup(PDFName.of("Annots"));
                    if (!annots || !(annots instanceof PDFArray) || annots.size() === 0) continue;

                    const { width: pageWidth, height: pageHeight } = page.getSize();
                    const pageEl = pageRefs.current[pageNum];
                    if (!pageEl) continue;

                    const rect = pageEl.getBoundingClientRect();
                    const domWidth = rect.width || 1;
                    const domHeight = rect.height || 1;

                    for (const annotRef of annots.asArray()) {
                        const annotDict = pdfDoc.context.lookup(annotRef);
                        if (!annotDict) continue;

                        const subtype = annotDict.get(PDFName.of("Subtype"));
                        const contents = annotDict.get(PDFName.of("Contents"));

                        if (
                            !subtype ||
                            !contents ||
                            !(
                                subtype === PDFName.of("FreeText") ||
                                subtype === PDFName.of("Text")
                            )
                        ) {
                            continue;
                        }

                        let text = "";
                        if (contents instanceof PDFString) {
                            text = contents.asString();
                        } else if (contents instanceof PDFHexString) {
                            text = contents.decodeText();
                        } else {
                            continue;
                        }

                        if (!text) continue;

                        const rectObj = annotDict.get(PDFName.of("Rect"));
                        if (!rectObj) continue;

                        const rectArr = rectObj.asArray
                            ? rectObj.asArray().map(Number)
                            : null;
                        if (!rectArr || rectArr.length !== 4) continue;

                        const [x1, y1, x2, y2] = rectArr;
                        const pdfWidth = x2 - x1;
                        const pdfHeight = y2 - y1;

                        const x = (x1 / pageWidth) * domWidth;
                        const width = (pdfWidth / pageWidth) * domWidth;

                        const yTopPdf = y2;
                        const yTopDom = domHeight * (1 - yTopPdf / pageHeight);
                        const height = (pdfHeight / pageHeight) * domHeight;
                        const y = yTopDom;

                        loadedAnnotations.push({
                            id: createId(),
                            pageNum,
                            x,
                            y,
                            width,
                            height,
                            tagName: text,
                        });
                    }
                }

                if (loadedAnnotations.length) {
                    setAnnotations(loadedAnnotations);
                }
            } catch (err) {
                console.error("Error leyendo anotaciones existentes del PDF", err);
            }
        },
        []
    );

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0 && acceptedFiles[0].type === "application/pdf") {
            setFile(acceptedFiles[0]);
            setAnnotations([]);
            setSelectedAnnotationId(null);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "application/pdf": [".pdf"] },
        maxFiles: 1,
        maxSize: 100 * 1024 * 1024,
        disabled: false,
    });

    useEffect(() => {
        setRoute(Routes.pdfAnnotationEditor);
    }, [setRoute]);

    const onDocumentLoadSuccess = useCallback(({ numPages }) => {
        setNumPages(numPages);
    }, []);

    useEffect(() => {
        if (file && numPages) {
            // Esperar a que React haya aplicado refs de página
            const timeout = setTimeout(() => {
                loadExistingAnnotationsFromPdf(file, numPages);
            }, 0);
            return () => clearTimeout(timeout);
        }
    }, [file, numPages, loadExistingAnnotationsFromPdf]);

    const getPageElement = (pageNum) => pageRefs.current[pageNum];

    const handlePageMouseDown = (e, pageNum) => {
        if (!e.target.closest(`[data-annotation-id]`)) {
            const pageEl = getPageElement(pageNum);
            if (!pageEl) return;
            const rect = pageEl.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            setDrawing({ pageNum, startX: x, startY: y, currentX: x, currentY: y });
        }
    };

    const handlePageMouseMove = (e, pageNum) => {
        if (drawing && drawing.pageNum === pageNum) {
            const pageEl = getPageElement(pageNum);
            if (!pageEl) return;
            const rect = pageEl.getBoundingClientRect();
            const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
            const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
            setDrawing((d) => ({ ...d, currentX: x, currentY: y }));
        }
    };

    const handlePageMouseUp = (e, pageNum) => {
        if (drawing && drawing.pageNum === pageNum) {
            const { startX, startY, currentX, currentY } = drawing;
            const x = Math.min(startX, currentX);
            const y = Math.min(startY, currentY);
            const width = Math.abs(currentX - startX);
            const height = Math.abs(currentY - startY);

            if (width > 5 && height > 5) {
                const id = createId();
                setAnnotations((prev) => [
                    ...prev,
                    {
                        id,
                        pageNum,
                        x,
                        y,
                        width,
                        height,
                        tagName: "",
                    },
                ]);
                setSelectedAnnotationId(id);
            }
            setDrawing(null);
        }
    };

    const handleTagChange = (id, tagName) => {
        setAnnotations((prev) =>
            prev.map((a) => (a.id === id ? { ...a, tagName } : a))
        );
    };

    const handleRemoveAnnotation = (id) => {
        setAnnotations((prev) => prev.filter((a) => a.id !== id));
        if (selectedAnnotationId === id) setSelectedAnnotationId(null);
    };

    const getDrawingBox = () => {
        if (!drawing) return null;
        const { startX, startY, currentX, currentY } = drawing;
        const x = Math.min(startX, currentX);
        const y = Math.min(startY, currentY);
        const width = Math.abs(currentX - startX);
        const height = Math.abs(currentY - startY);
        return { x, y, width, height };
    };

    const handleDownloadPdf = async () => {
        if (!file) return;

        // Si no hay anotaciones, descargamos el PDF original
        if (!annotations.length) {
            const url = URL.createObjectURL(file);
            const a = document.createElement("a");
            a.href = url;
            a.download = file.name || "documento.pdf";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            return;
        }

        try {
            const existingPdfBytes = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(existingPdfBytes);

            // Agrupar anotaciones visibles por página y quedarnos solo con las que tienen tagName
            const annotationsByPage = annotations
                .filter((a) => a.tagName)
                .reduce((acc, ann) => {
                    const pageNum = ann.pageNum || 1;
                    if (!acc[pageNum]) acc[pageNum] = [];
                    acc[pageNum].push(ann);
                    return acc;
                }, {});

            const annotsKey = PDFName.of("Annots");

            // Recorremos solo las páginas que tienen anotaciones activas en el editor
            for (const [pageNumStr, pageAnns] of Object.entries(annotationsByPage)) {
                const pageNum = Number(pageNumStr);
                const pageIndex = pageNum - 1;
                const page = pdfDoc.getPage(pageIndex);
                if (!page) continue;

                const { width: pageWidth, height: pageHeight } = page.getSize();
                const pageEl = pageRefs.current[pageNum];
                if (!pageEl) continue;

                const rect = pageEl.getBoundingClientRect();
                const domWidth = rect.width || 1;
                const domHeight = rect.height || 1;

                // Limpiar tags anteriores de este tipo en la página:
                // eliminamos anotaciones FreeText/Text cuyo Contents empiece por "Tag"
                let existingAnnots = page.node.lookup(annotsKey);
                let annotsArr;
                if (existingAnnots) {
                    annotsArr = pdfDoc.context.obj([]);
                    for (let i = 0; i < existingAnnots.size(); i++) {
                        const annotRef = existingAnnots.get(i);
                        const annotDict = pdfDoc.context.lookup(annotRef);
                        if (!annotDict) continue;

                        const subtype = annotDict.get(PDFName.of("Subtype"));
                        const contents = annotDict.get(PDFName.of("Contents"));

                        if (
                            subtype &&
                            (subtype === PDFName.of("FreeText") ||
                                subtype === PDFName.of("Text")) &&
                            contents &&
                            (contents instanceof PDFString || contents instanceof PDFHexString)
                        ) {
                            const text =
                                contents instanceof PDFString
                                    ? contents.asString()
                                    : contents.decodeText();
                            if (text && text.startsWith("Tag")) {
                                // Es un tag de firma anterior → lo descartamos
                                continue;
                            }
                        }

                        annotsArr.push(annotRef);
                    }
                } else {
                    annotsArr = pdfDoc.context.obj([]);
                }

                page.node.set(annotsKey, annotsArr);

                // Crear las nuevas anotaciones invisibles para esta página
                for (const ann of pageAnns) {
                    const relX = ann.x / domWidth;
                    const relYTop = ann.y / domHeight;
                    const relWidth = ann.width / domWidth;
                    const relHeight = ann.height / domHeight;

                    const drawWidth = relWidth * pageWidth;
                    const drawHeight = relHeight * pageHeight;
                    const drawX = relX * pageWidth;
                    const drawY = pageHeight * (1 - relYTop - relHeight);

                    const pdfRect = [drawX, drawY, drawX + drawWidth, drawY + drawHeight];

                    const annotationDict = pdfDoc.context.obj({
                        Type: PDFName.of("Annot"),
                        Subtype: PDFName.of("FreeText"),
                        Rect: pdfDoc.context.obj(pdfRect.map((coord) => PDFNumber.of(coord))),
                        Contents: PDFString.of(String(ann.tagName)),
                        CA: PDFNumber.of(0), // opacidad 0 → invisible
                        DA: PDFString.of("/F1 1 Tf 0 0 0 rg"),
                    });

                    const annotationRef = pdfDoc.context.register(annotationDict);
                    annotsArr.push(annotationRef);
                }
            }

            const modifiedBytes = await pdfDoc.save();
            const blob = new Blob([modifiedBytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const baseName = file.name?.replace(/\.pdf$/i, "") || "documento";
            a.href = url;
            a.download = `${baseName}-annotated.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error("Error generando PDF anotado", e);
        }
    };

    return (
        <section className={styles.section}>
            {!file ? (
                <div
                    {...getRootProps()}
                    className={`${styles.dropZone} ${isDragActive ? styles.dropZoneActive : ""}`}
                >
                    <input {...getInputProps()} />
                    <Typography color="textSecondary">
                        Arrastra un PDF aquí o haz clic para seleccionar
                    </Typography>
                </div>
            ) : (
                <Box className={styles.editorLayout}>
                    <Box className={styles.pdfArea}>
                        <Document
                            file={file}
                            onLoadSuccess={onDocumentLoadSuccess}
                            className={styles.document}
                        >
                            {numPages && Array.from(new Array(numPages), (_, i) => i + 1).map(
                                (pageNum) => (
                                    <Box
                                        key={pageNum}
                                        ref={(el) => (pageRefs.current[pageNum] = el)}
                                        className={styles.pageWrapper}
                                        onMouseDown={(e) => handlePageMouseDown(e, pageNum)}
                                        onMouseMove={(e) => handlePageMouseMove(e, pageNum)}
                                        onMouseUp={(e) => handlePageMouseUp(e, pageNum)}
                                        onMouseLeave={() =>
                                            drawing?.pageNum === pageNum && setDrawing(null)
                                        }
                                    >
                                        <Page
                                            pageNumber={pageNum}
                                            renderTextLayer={true}
                                            renderAnnotationLayer={true}
                                            className={styles.page}
                                        />
                                        {annotations
                                            .filter((a) => a.pageNum === pageNum)
                                            .map((a) => (
                                                <div
                                                    key={a.id}
                                                    data-annotation-id={a.id}
                                                    className={`${styles.annotationBox} ${
                                                        selectedAnnotationId === a.id
                                                            ? styles.annotationBoxSelected
                                                            : ""
                                                    }`}
                                                    style={{
                                                        left: a.x,
                                                        top: a.y,
                                                        width: a.width,
                                                        height: a.height,
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedAnnotationId(a.id);
                                                    }}
                                                />
                                            ))}
                                        {drawing?.pageNum === pageNum && getDrawingBox() && (
                                            <div
                                                className={styles.drawingBox}
                                                style={{
                                                    left: getDrawingBox().x,
                                                    top: getDrawingBox().y,
                                                    width: getDrawingBox().width,
                                                    height: getDrawingBox().height,
                                                }}
                                            />
                                        )}
                                    </Box>
                                )
                            )}
                        </Document>
                    </Box>

                    <Paper className={styles.tagsPanel} elevation={1}>
                        <Typography variant="subtitle1" className={styles.tagsTitle}>
                            Etiquetas
                        </Typography>

                        {annotations.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                                Dibuja un recuadro en el PDF para agregar una etiqueta
                            </Typography>
                        ) : (
                            <Box className={styles.tagsList}>
                                {annotations.map((a) => (
                                    <Box
                                        key={a.id}
                                        className={`${styles.tagItem} ${
                                            selectedAnnotationId === a.id
                                                ? styles.tagItemSelected
                                                : ""
                                        }`}
                                    >
                                        <TextField
                                            label={`Pag. ${a.pageNum}`}
                                            size="small"
                                            value={a.tagName}
                                            onChange={(e) =>
                                                handleTagChange(a.id, e.target.value)
                                            }
                                            onClick={() => setSelectedAnnotationId(a.id)}
                                            fullWidth
                                            placeholder="Nombre del Tag"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveAnnotation(a.id)}
                                            color="error"
                                            aria-label="Eliminar"
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        <Box marginTop={3} display="flex" gap={1}>
                            
                        <Button
                                size="big"
                                onClick={() => {
                                    setFile(null);
                                    setAnnotations([]);
                                    setSelectedAnnotationId(null);
                                    setNumPages(null);
                                }}
                                text="Cancelar"
                            />
                            <Button
                                size="big"
                                onClick={handleDownloadPdf}
                                text="Descargar PDF"
                            />
                        </Box>
                    </Paper>
                </Box>
            )}
        </section>
    );
};

export default PdfAnnotationEditor;
